import type { SelectOption } from '@aus-platform/design-system';
import proj4 from 'proj4';
import { EPSGCode, WGS84Crs } from 'src/shared/enums';
import { isCRSDefined } from 'src/shared/helpers/crs-helpers';
import { CoordinateFieldLabel } from './enums';
import type { CoordinateFieldsConfig, CoordinateRange, GeographicCoordinates2D } from './types';

// WGS 84 geographic is the backward-compatible default selection.
export const WGS84_CRS_OPTION: SelectOption = {
  label: `EPSG:${WGS84Crs.epsgCode} / ${WGS84Crs.name}`,
  value: WGS84Crs.epsgCode,
};

const WGS84_BOUNDS = {
  latitudeMin: -90,
  latitudeMax: 90,
  longitudeMin: -180,
  longitudeMax: 180,
};

/**
 * Restricts free-text input to a numeric value: digits, at most one decimal
 * point, and (when allowed) a single leading minus sign. Everything else is
 * stripped. Applied on both change and paste so the rules are identical.
 *
 * @param rawValue - The raw input/pasted string.
 * @param allowNegative - Whether a leading minus sign is permitted.
 * @returns The sanitized string.
 */
export const sanitizeNumericInput = (
  rawValue: string,
  allowNegative: boolean,
): string => {
  // Drop any character that is not a digit, dot, or minus sign.
  let sanitized = rawValue.replace(/[^0-9.-]/g, '');

  // Handle the sign: keep a single leading minus only when allowed.
  const isNegative = allowNegative && sanitized.startsWith('-');
  sanitized = sanitized.replace(/-/g, '');

  // Keep only the first decimal point.
  const firstDotIndex = sanitized.indexOf('.');
  if (firstDotIndex !== -1) {
    sanitized =
      sanitized.slice(0, firstDotIndex + 1) +
      sanitized.slice(firstDotIndex + 1).replace(/\./g, '');
  }

  return (isNegative ? '-' : '') + sanitized;
};

/**
 * Parses a coordinate field string into a finite number, treating empty or
 * incomplete entries (e.g. "", "-", ".") as not-a-number.
 *
 * @param value - The field's raw string value.
 * @returns The parsed finite number, or null if the value is empty/incomplete.
 */
export const toFiniteNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '' || trimmed === '-' || trimmed === '.' || trimmed === '-.') {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Builds the presentation config (labels, sign rules) for the two coordinate
 * inputs based on whether the selected CRS is geographic or projected.
 *
 * @param isGeographic - Whether the selected CRS is geographic.
 * @returns The fields configuration.
 */
export const getCoordinateFieldsConfig = (
  isGeographic: boolean,
): CoordinateFieldsConfig => {
  if (isGeographic) {
    return {
      isGeographic: true,
      primaryLabel: CoordinateFieldLabel.Latitude,
      secondaryLabel: CoordinateFieldLabel.Longitude,
      primaryAllowsNegative: true,
      secondaryAllowsNegative: true,
    };
  }
  return {
    isGeographic: false,
    primaryLabel: CoordinateFieldLabel.Easting,
    secondaryLabel: CoordinateFieldLabel.Northing,
    primaryAllowsNegative: false,
    secondaryAllowsNegative: false,
  };
};

/**
 * Whether a CRS is ready for transformation (its proj4 definition exists).
 * Geographic WGS 84 is always available.
 */
export const isCrsReadyForTransform = (epsgCode: string | number): boolean =>
  isCRSDefined(Number(epsgCode));

/**
 * Reprojects a point to WGS 84 (longitude/latitude) using proj4.
 *
 * proj4 always treats the array as [x, y] = [easting/longitude, northing/
 * latitude] regardless of the CRS's declared axis order, so callers must pass
 * the values in that order.
 *
 * @param epsgCode - Source EPSG code (must be registered with proj4).
 * @param x - Easting (projected) or longitude (geographic).
 * @param y - Northing (projected) or latitude (geographic).
 * @returns The WGS 84 coordinates.
 * @throws Error if the transformation fails.
 */
export const reprojectToWgs84 = (
  epsgCode: string | number,
  x: number,
  y: number,
): GeographicCoordinates2D => {
  const [longitude, latitude] = proj4(
    `EPSG:${epsgCode}`,
    `EPSG:${EPSGCode.WGS84}`,
    [x, y],
  );
  return { longitude, latitude };
};

const isInRange = (value: number, min: number, max: number): boolean =>
  value >= min && value <= max;

const hasProjectedRange = (
  range?: CoordinateRange,
): range is CoordinateRange & {
  eastingMin: number;
  eastingMax: number;
  northingMin: number;
  northingMax: number;
} =>
  range?.eastingMin != null &&
  range?.eastingMax != null &&
  range?.northingMin != null &&
  range?.northingMax != null;

const isWithinWorldBounds = ({
  latitude,
  longitude,
}: GeographicCoordinates2D): boolean =>
  Number.isFinite(latitude) &&
  Number.isFinite(longitude) &&
  isInRange(latitude, WGS84_BOUNDS.latitudeMin, WGS84_BOUNDS.latitudeMax) &&
  isInRange(longitude, WGS84_BOUNDS.longitudeMin, WGS84_BOUNDS.longitudeMax);

export type CoordinateEvaluation = {
  isValid: boolean;
  coordinates?: GeographicCoordinates2D;
  latitudeOrEastingInvalid: boolean;
  longitudeOrNorthingInvalid: boolean;
};

const invalidBothFields = (): CoordinateEvaluation => ({
  isValid: false,
  latitudeOrEastingInvalid: true,
  longitudeOrNorthingInvalid: true,
});

/**
 * Evaluates the two coordinate inputs for the selected CRS, deciding whether
 * "Go to point" can be enabled and which fields (if any) should show an error.
 *
 * Each field is validated independently as soon as it has content. The button
 * enables only when both fields hold valid in-range numbers and transform succeeds.
 *
 * Geographic CRS: latitude/longitude checked against WGS 84 bounds.
 * Projected CRS: easting/northing checked against backend native-unit ranges.
 */
export const evaluateCoordinates = (params: {
  isGeographic: boolean;
  epsgCode: string | number;
  latitudeOrEasting: string;
  longitudeOrNorthing: string;
  range?: CoordinateRange;
}): CoordinateEvaluation => {
  const {
    isGeographic,
    epsgCode,
    latitudeOrEasting,
    longitudeOrNorthing,
    range,
  } = params;

  if (isGeographic) {
    const latitudeInput = latitudeOrEasting;
    const longitudeInput = longitudeOrNorthing;
    const latitude = toFiniteNumber(latitudeInput);
    const longitude = toFiniteNumber(longitudeInput);

    const latitudeHasContent = latitudeInput.trim() !== '';
    const longitudeHasContent = longitudeInput.trim() !== '';

    let latitudeInvalid = latitudeHasContent && latitude === null;
    let longitudeInvalid = longitudeHasContent && longitude === null;

    if (latitude !== null) {
      latitudeInvalid ||= !isInRange(
        latitude,
        WGS84_BOUNDS.latitudeMin,
        WGS84_BOUNDS.latitudeMax,
      );
    }
    if (longitude !== null) {
      longitudeInvalid ||= !isInRange(
        longitude,
        WGS84_BOUNDS.longitudeMin,
        WGS84_BOUNDS.longitudeMax,
      );
    }

    const bothReady =
      latitude !== null &&
      longitude !== null &&
      !latitudeInvalid &&
      !longitudeInvalid;

    if (!bothReady) {
      return {
        isValid: false,
        latitudeOrEastingInvalid: latitudeInvalid,
        longitudeOrNorthingInvalid: longitudeInvalid,
      };
    }

    if (!isCrsReadyForTransform(epsgCode)) {
      return {
        isValid: false,
        latitudeOrEastingInvalid: false,
        longitudeOrNorthingInvalid: false,
      };
    }

    try {
      const coordinates =
        String(epsgCode) === String(EPSGCode.WGS84)
          ? { latitude, longitude }
          : reprojectToWgs84(epsgCode, longitude, latitude);
      return isWithinWorldBounds(coordinates)
        ? {
            isValid: true,
            coordinates,
            latitudeOrEastingInvalid: false,
            longitudeOrNorthingInvalid: false,
          }
        : invalidBothFields();
    } catch {
      return invalidBothFields();
    }
  }

  const eastingInput = latitudeOrEasting;
  const northingInput = longitudeOrNorthing;
  const easting = toFiniteNumber(eastingInput);
  const northing = toFiniteNumber(northingInput);

  const eastingHasContent = eastingInput.trim() !== '';
  const northingHasContent = northingInput.trim() !== '';

  let eastingInvalid = eastingHasContent && easting === null;
  let northingInvalid = northingHasContent && northing === null;

  if (hasProjectedRange(range)) {
    if (easting !== null) {
      eastingInvalid ||= !isInRange(easting, range.eastingMin, range.eastingMax);
    }
    if (northing !== null) {
      northingInvalid ||= !isInRange(
        northing,
        range.northingMin,
        range.northingMax,
      );
    }
  }

  const bothReady =
    easting !== null && northing !== null && !eastingInvalid && !northingInvalid;

  if (!bothReady) {
    return {
      isValid: false,
      latitudeOrEastingInvalid: eastingInvalid,
      longitudeOrNorthingInvalid: northingInvalid,
    };
  }

  if (!isCrsReadyForTransform(epsgCode)) {
    return {
      isValid: false,
      latitudeOrEastingInvalid: false,
      longitudeOrNorthingInvalid: false,
    };
  }

  try {
    const coordinates = reprojectToWgs84(epsgCode, easting, northing);

    if (!hasProjectedRange(range) && !isWithinWorldBounds(coordinates)) {
      return invalidBothFields();
    }

    return {
      isValid: true,
      coordinates,
      latitudeOrEastingInvalid: false,
      longitudeOrNorthingInvalid: false,
    };
  } catch {
    return invalidBothFields();
  }
};
